import {
	createSlice,
	createAsyncThunk,
	createSelector,
} from "@reduxjs/toolkit";
import { getToken, getUserPermissionByAppId } from "../api";
import dayjs from "dayjs";
import { CME_CONFIG } from "features/cme/constants";

export const login = createAsyncThunk(
	"security/login",
	async (_, { rejectWithValue }) => {
		try {
			return await getToken();
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const fetchPermission = createAsyncThunk(
	"security/fetchPermission",
	async ({ appId, userId = null }, { dispatch, rejectWithValue }) => {
		try {
			const permission = await getUserPermissionByAppId({
				appId,
				userId,
			});
			//if userId is not supplied, it's for my own permission
			if (!userId) dispatch(permissionEdited(permission));
			else dispatch(forPersonPermissionEdited(permission));
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

const securitySlice = createSlice({
	name: "security",
	initialState: {
		user: {
			delegateUsers: [],
		},
		authToken: null,
		status: "idle",
		error: null,
		permission: [],
		forPersonPermission: [],
		permissionLoaded: false,
		selectedPermission: {},
	},
	reducers: {
		permissionEdited(state, action) {
			state.permission = action.payload;
		},
		permissionSelected(state, action) {
			state.selectedPermission = action.payload;
		},
		forPersonPermissionEdited(state, action) {
			state.forPersonPermission = action.payload;
		},
	},
	extraReducers: {
		[login.pending]: (state, action) => {
			state.status = "loading";
		},
		[login.fulfilled]: (state, action) => {
			state.status = "succeeded";
			const { authToken, user } = action.payload;
			state.authToken = authToken;
			state.user = { ...user, personId: 5182 };
			state.user = user;
			state.error = null;
		},
		[login.rejected]: (state, action) => {
			state.status = "failed";
			state.error = { ...action.payload };
			state.authToken = null;
		},
		[fetchPermission.pending]: (state, action) => {
			state.status = "loading";
			state.error = null;
		},
		[fetchPermission.fulfilled]: (state, action) => {
			state.status = "succeeded";
			state.error = null;
			state.permissionLoaded = true;
		},
		[fetchPermission.rejected]: (state, action) => {
			state.status = "failed";
			state.error = { ...action.payload };
		},
	},
});

export const {
	permissionEdited,
	permissionSelected,
	forPersonPermissionEdited,
} = securitySlice.actions;

export const selectSecurityStatus = (state) => state.security.status;
export const selectSecurityError = (state) => state.security.error;
export const selectUser = (state) => state.security.user;
export const selectAuthToken = (state) => state.security.authToken;
export const selectPersonId = (state) => state.security.user.personId;
export const selectAppPermission = (state) => state.security.permission || [];
export const selectForPersonAppPermission = (state) =>
	state.security.forPersonPermission || [];
export const selectCurrentPermission = (state) =>
	state.security.selectedPermission;
export const selectPermissionLoaded = (state) =>
	state.security.permissionLoaded;
export const selectDelegateUsers = (state) =>
	state.security.user?.delegateUsers || [];

export const selectUserOrganisation = createSelector([selectUser], (user) => {
	return user.organisation;
});

//#region CME
export const selectIsCmeApprover = createSelector(
	[selectAppPermission],
	(p) => {
		//app admins always have access
		//privilege of 2 is team access, 3 is deep team access
		return p
			? p.some((i) => i.isAppAdmin || i.privilege >= 2 || i.isSystemAdmin)
			: false;
	}
);
export const selectIsCmeAdmin = createSelector(
	[selectAppPermission, selectUser],
	(p, u) => {
		return u.isSystemAdmin
			? true
			: p
			? p.some((i) => i.isAppAdmin || i.isSystemAdmin)
			: false;
	}
);
export const selectIsApprover = createSelector([selectAppPermission], (p) => {
	//app admins always have access
	//privilege of 2 is team access, 3 is deep team access
	return p
		? p.some((i) => i.isAppAdmin || i.privilege >= 2 || i.isSystemAdmin)
		: false;
});
export const selectIsAppAdmin = createSelector(
	[selectAppPermission, selectUser],
	(p, u) => {
		return u?.isSystemAdmin
			? true
			: p
			? p.some((i) => i.isAppAdmin || i.isSystemAdmin)
			: false;
	}
);
export const selectIsCmeUser = createSelector([selectAppPermission], (p) => {
	//any access to cme app denotes a user
	return p ? p.length > 0 : false;
});
export const selectIsCmeClaimant = createSelector(
	[selectAppPermission, selectUserOrganisation],
	(p, org) => {
		const today = dayjs();
		return p.length > 0
			? p.some((i) => {
					//you cannot use CME from x months prior to your end date
					const x = CME_CONFIG[org]?.DisableCmeXMonthsBeforeEndDate;
					const xMonthsBeforeEndDate = dayjs(
						i.endDate,
						"YYYY-MM-DD"
					).subtract(x, "month");
					return i.roleName === "SMO" && xMonthsBeforeEndDate > today;
			  })
			: false;
	}
);
export const selectIsCpdClaimant = createSelector(
	[selectAppPermission, selectUserOrganisation],
	(p, org) => {
		const today = dayjs();
		return p.length > 0
			? p.some((i) => {
					//you cannot use CME from x months prior to your end date
					const x = CME_CONFIG[org]?.DisableCmeXMonthsBeforeEndDate;
					const xMonthsBeforeEndDate = dayjs(
						i.endDate,
						"YYYY-MM-DD"
					).subtract(x, "month");
					return (
						i.roleName === "Nurse Practitioner" &&
						xMonthsBeforeEndDate > today
					);
			  })
			: false;
	}
);
export const selectIsCmeCoordinator = createSelector(
	[selectAppPermission],
	(p) => {
		return p
			? p.some((i) =>
					[
						"SMO Leave & Reimbursement Admin",
						"CME Coordinator",
					].includes(i.roleName)
			  )
			: false;
	}
);
export const selectIsSysAdmin = createSelector(
	[selectAppPermission, selectUser],
	(p, u) => {
		return u.isSystemAdmin
			? true
			: p
			? p.some((i) => i.isSystemAdmin)
			: false;
	}
);
export const selectCmeUserTeam = createSelector([selectAppPermission], (p) => {
	return p.filter((i) => i.actions?.some((a) => a === "Write"));
});
export const selectCmeForUserTeam = createSelector(
	[selectForPersonAppPermission],
	(p) => {
		return p.filter((i) => i.actions?.some((a) => a === "Write"));
	}
);
//#endregion CME
export default securitySlice.reducer;
